import { type FormEvent, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCompetition } from '@/hooks/useCompetitions';
import { useCategories, useCreateCategory, useDeleteCategory } from '@/hooks/useCategories';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';

export function OrganizerCategoriesPage() {
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const competition = useCompetition(competitionId);
  const categories = useCategories(competitionId);
  const createCategory = useCreateCategory(competitionId);
  const deleteCategory = useDeleteCategory(competitionId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [maxSubmissionsPerUser, setMaxSubmissionsPerUser] = useState('3');

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const parsedMax = Math.max(1, parseInt(maxSubmissionsPerUser, 10) || 1);
    await createCategory.mutateAsync({ name, description, maxSubmissionsPerUser: parsedMax });
    setName('');
    setDescription('');
    setMaxSubmissionsPerUser('3');
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/organizer/competitions" className="text-sm text-fg-muted hover:underline">
          &larr; Nazaj na tekmovanja
        </Link>
        <h1 className="text-xl font-semibold text-fg">
          Kategorije {competition.data ? `za "${competition.data.name}"` : ''}
        </h1>
      </div>

      <Card>
        <CardBody>
          <form onSubmit={handleCreate} className="flex flex-col gap-3 sm:flex-row sm:items-end">
            {createCategory.isError && (
              <Alert variant="error">{getApiErrorMessage(createCategory.error)}</Alert>
            )}
            <div className="flex-1">
              <Input label="Naziv kategorije" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="flex-1">
              <Input
                label="Opis (neobvezno)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-40">
              <Input
                label="Največ prispevkov/uporabnika"
                type="number"
                min={1}
                value={maxSubmissionsPerUser}
                onChange={(e) => setMaxSubmissionsPerUser(e.target.value)}
              />
            </div>
            <Button type="submit" isLoading={createCategory.isPending}>
              Dodaj kategorijo
            </Button>
          </form>
        </CardBody>
      </Card>

      {deleteCategory.isError && <Alert variant="error">{getApiErrorMessage(deleteCategory.error)}</Alert>}

      {categories.isLoading && <PageSpinner />}
      {categories.isError && (
        <ErrorState message={getApiErrorMessage(categories.error)} onRetry={() => categories.refetch()} />
      )}
      {categories.isSuccess && categories.data.items.length === 0 && (
        <EmptyState title="Ni še kategorij" description="Zgoraj dodajte kategorijo." />
      )}
      {categories.isSuccess && categories.data.items.length > 0 && (
        <div className="flex flex-col gap-2">
          {categories.data.items.map((cat) => (
            <Card key={cat.id}>
              <CardBody className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-fg">{cat.name}</p>
                  {cat.description && <p className="text-sm text-fg-muted">{cat.description}</p>}
                  <p className="text-xs text-fg-muted">
                    Največ {cat.maxSubmissionsPerUser ?? 1} prispevek/prispevkov na tekmovalca
                  </p>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  isLoading={deleteCategory.isPending && deleteCategory.variables === cat.id}
                  onClick={() => {
                    if (confirm(`Izbriši kategorijo "${cat.name}"?`)) deleteCategory.mutate(cat.id);
                  }}
                >
                  Izbriši
                </Button>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
